"use client";

import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { SDUIOnViewActionsHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/SDUIOnViewActionHandler";
import { SEARCH_IRRELEVANCE_CAROUSEL_COMPONENT_NAME } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/consts";
import { logLegacyViewEvent } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/EventLogging/logLegacyViewEvent";
import { logSDUIEvent } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import { SDUIProductTileImpressionProvider } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIProductTileImpressionProvider";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
  SDUIComponentInstanceRendererProps,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { handleSearchIrrelevantCarouselOnViewActions } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Carousel/__internal__/carouselActions";
import {
  getCarouselControlsVariant,
  getCarouselDragFree,
  getCarouselLoop,
  getCarouselShowNavigationArrows,
  getCarouselTileSpacing,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Carousel/__internal__/carouselStyleUtils";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { MaybeServerRenderedCarouselTileWrapper } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/ProductTile/MaybeServerRenderedCarouselTileWrapper";
import { SDUITileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/SDUITileRenderer";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useRecordMilestoneByDay } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import {
  Carousel,
  ITEM_PER_SLIDE_DEFAULT,
} from "@faire/web--source/slate-labs/Carousel/Carousel";
import { Column } from "@faire/web--source/slate/Layout";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { trackFlashSalesWarehouseSaleHomepageCarouselView } from "@faire/web-api--source/events/flashSales/view/warehouseSaleHomepageCarousel";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { ICarouselComponent } from "@faire/web-api--source/indigofair/data/ICarouselComponent";
import { ICarouselStyle } from "@faire/web-api--source/indigofair/data/ICarouselStyle";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import React, { Suspense, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

interface IProps
  extends Pick<
    SDUIComponentInstanceRendererProps,
    "loggingData" | "renderBehaviour"
  > {
  tiles: ITile[];
  style: ICarouselComponent["style"];
  numberOfRows?: number;
  shouldOverEstimateFlagOnSSR?: boolean;
  onViewActions?: IAction[];
  hasDynamicRows?: boolean;
}

const DEFAULT_SDUI_CAROUSEL_PEEK_NEXT_PERCENT = {
  mobileAndAbove: 20,
  tabletAndAbove: 0,
};

const DEFAULT_SDUI_CAROUSEL_ITEM_GAP = {
  mobileAndAbove: 8,
  tabletAndAbove: 16,
};

export const SDUICarousel: React.FC<IProps> = ({
  tiles,
  style,
  numberOfRows = 1,
  loggingData,
  renderBehaviour,
  shouldOverEstimateFlagOnSSR = false,
  onViewActions,
  hasDynamicRows = false,
}) => {
  const history = useHistory();
  const recordMilestoneOnceByDay = useRecordMilestoneByDay();
  const { clearAllFilters } = useFilterStore([]);
  const maybeAssignCategoryTreeExperiment =
    useMaybeAssignCategoryTreeExperiment();
  const retailerToken = getRetailer()?.token;
  const {
    data: { isSideBarVisible },
  } = useFilterStore(["isSideBarVisible"]);

  useEffect(() => {
    if (
      retailerToken &&
      loggingData.component_type === "WAREHOUSE_SALE_CAROUSEL" &&
      loggingData.view_display_page === DisplayPageType.HOMEPAGE
    ) {
      trackFlashSalesWarehouseSaleHomepageCarouselView(retailerToken);
    }
  }, [
    retailerToken,
    loggingData.component_type,
    loggingData.view_display_page,
  ]);

  const {
    loop_behavior,
    navigation_control,
    tile_spacing,
    transition_style,
    tile_width = ICarouselStyle.ITileWidth.build(),
  } = style ?? ICarouselStyle.build();

  const { fractional } = tile_width;
  const responsiveTilesPerSlide: ResponsiveValues<number> | undefined =
    fractional?.responsive_tiles_per_slide
      ? maybeUpdateNumberOfTilesPerSlide(
          convertServerResponsivePropertyToClientResponsiveProperty(
            ITEM_PER_SLIDE_DEFAULT.mobileAndAbove,
            (value) => Math.floor(value),
            fractional?.responsive_tiles_per_slide
          ),
          isSideBarVisible,
          loggingData
        )
      : undefined;

  const responsivePeekNextPercent: ResponsiveValues<number> | undefined =
    fractional?.responsive_tiles_per_slide
      ? convertServerResponsivePropertyToClientResponsiveProperty(
          ITEM_PER_SLIDE_DEFAULT.mobileAndAbove,
          (value) => Math.round((value % 1) * 100),
          fractional?.responsive_tiles_per_slide
        )
      : DEFAULT_SDUI_CAROUSEL_PEEK_NEXT_PERCENT;

  const isFreeFlow = tile_width?.content !== undefined;

  const currentNumTilesPerSlide = useResponsiveValue(
    responsiveTilesPerSlide ?? ITEM_PER_SLIDE_DEFAULT
  );

  const onVisibleCallback = (newVisibleColumns: ITile[][]) => {
    const items = newVisibleColumns.flat();
    const actionContent = items
      .map((item) => item.logging_data?.instance_identifier)
      .join(" ");
    const actionPositions = items
      .map((item) => item.logging_data?.tile_position)
      .join(" ");

    items.forEach((item) => {
      SDUIOnViewActionsHandler(
        item.on_view ?? [],
        loggingData,
        history,
        recordMilestoneOnceByDay,
        clearAllFilters,
        maybeAssignCategoryTreeExperiment
      );
    });

    logSDUIEvent(
      "VIEW",
      ActionLoggingEventParams.build({
        ...loggingData,
        action_content: actionContent,
        action_positions: actionPositions,
      })
    );

    if (loggingData.view_display_page) {
      // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
      // logLegacyViewEvent should be removed once banners and grids are fully migrated to SDUI
      // and data pipelines and dashboards no longer need the layout element events
      logLegacyViewEvent(loggingData.view_display_page, {
        componentName: loggingData.component_name ?? "",
        requestId: loggingData.request_id ?? "",
        actionContent,
      });

      if (
        loggingData.view_display_page === DisplayPageType.SEARCH_PAGE &&
        loggingData.component_name ===
          SEARCH_IRRELEVANCE_CAROUSEL_COMPONENT_NAME &&
        onViewActions?.length &&
        actionPositions
      ) {
        handleSearchIrrelevantCarouselOnViewActions(
          onViewActions,
          actionPositions,
          currentNumTilesPerSlide
        );
      }
    }
  };

  const currentNumberOfRows = useMemo(
    () =>
      maybeRecalculateNumberOfRows(
        hasDynamicRows,
        tiles,
        numberOfRows,
        currentNumTilesPerSlide
      ),
    [hasDynamicRows, tiles, numberOfRows, currentNumTilesPerSlide]
  );

  const chunkedTiles = useMemo(
    () => chunkTiles(tiles, currentNumberOfRows),
    [tiles, currentNumberOfRows]
  );

  return (
    <SDUIProductTileImpressionProvider loggingData={loggingData}>
      <Carousel
        noMobilePadding={
          loggingData?.section_type === "FilterablePaginatedSection"
        }
        shouldOverEstimateFlagOnSSR={shouldOverEstimateFlagOnSSR}
        items={chunkedTiles}
        itemsPerSlide={responsiveTilesPerSlide}
        peekNextPercent={responsivePeekNextPercent}
        renderItem={(tilesColumn, index) => {
          if (tilesColumn.length > 1) {
            return (
              <Column className="gap-4">
                {tilesColumn.map((tile) => (
                  <SDUITileRenderer
                    key={tile.logging_data?.instance_identifier}
                    tileElement={tile}
                    loggingData={loggingData}
                  />
                ))}
              </Column>
            );
          } else if (tilesColumn[0]) {
            return tilesColumn[0].product_with_brand_info_tile ? (
              <Suspense fallback={null}>
                <MaybeServerRenderedCarouselTileWrapper
                  tileElement={tilesColumn[0]}
                  loggingData={ActionLoggingEventParams.build({
                    ...loggingData,
                    action_content:
                      tilesColumn[0].logging_data?.instance_identifier,
                    action_positions:
                      tilesColumn[0].logging_data?.tile_position?.toString(),
                    action_instance_identifier:
                      tilesColumn[0].logging_data?.instance_identifier,
                  })}
                  index={index}
                />
              </Suspense>
            ) : (
              <SDUITileRenderer
                tileElement={tilesColumn[0]}
                loggingData={loggingData}
              />
            );
          }
          return null;
        }}
        itemGap={
          getCarouselTileSpacing(tile_spacing) ?? DEFAULT_SDUI_CAROUSEL_ITEM_GAP
        }
        loop={getCarouselLoop(loop_behavior)}
        dragFree={getCarouselDragFree(transition_style)}
        showNavigationArrows={getCarouselShowNavigationArrows(
          navigation_control,
          loggingData
        )}
        arrowColorOrVariant={getCarouselControlsVariant(navigation_control)}
        shouldShowDisabledNavigationArrows
        onVisible={onVisibleCallback}
        freeFlow={isFreeFlow}
        arrowOffsetX={0}
        attachRefOnView={renderBehaviour?.deferInitializationUntilView}
      />
    </SDUIProductTileImpressionProvider>
  );
};

// Reduces the number of rows in a dynamic carousel if there are not enough tiles in the current viewport to fill the space.
// The threshold to swap is based on the N number of tiles per row at that viewport.
// If we have fewer than 2N - 1 tiles available to fill the carousel, we should reduce the number of rows.
const maybeRecalculateNumberOfRows = (
  hasDynamicRows: boolean,
  tiles: ITile[],
  numberOfRows: number,
  currentNumTilesPerSlide: number
) => {
  if (hasDynamicRows && tiles.length < currentNumTilesPerSlide * 2 - 1) {
    return numberOfRows - 1;
  }
  return numberOfRows;
};

const chunkTiles = (tiles: ITile[], numberOfRows: number): ITile[][] => {
  return tiles.reduce<ITile[][]>((arr, item, idx) => {
    return idx % numberOfRows === 0
      ? [...arr, [item]]
      : [...arr.slice(0, -1), [...(arr.slice(-1)[0] ?? []), item]];
  }, []);
};

const maybeUpdateNumberOfTilesPerSlide = (
  responsiveTilesPerSlide: ResponsiveValues<number> | undefined,
  isSideBarVisible: boolean,
  loggingData?: ComponentLoggingEventParams
) => {
  if (
    !isSideBarVisible ||
    !responsiveTilesPerSlide ||
    !loggingData ||
    loggingData.view_display_page !== DisplayPageType.SEARCH_PAGE ||
    loggingData.section_type !== "FilterablePaginatedSection"
  ) {
    return responsiveTilesPerSlide;
  }

  if (
    !responsiveTilesPerSlide.desktopAndAbove &&
    !responsiveTilesPerSlide.xLargeAndAbove &&
    !responsiveTilesPerSlide.xxLargeDesktop
  ) {
    return responsiveTilesPerSlide;
  }

  return {
    ...responsiveTilesPerSlide,
    desktopAndAbove: responsiveTilesPerSlide.desktopAndAbove
      ? responsiveTilesPerSlide.desktopAndAbove - 1
      : undefined,
    xLargeAndAbove: responsiveTilesPerSlide.xLargeAndAbove
      ? responsiveTilesPerSlide.xLargeAndAbove - 1
      : undefined,
    xxLargeDesktop: responsiveTilesPerSlide.xxLargeDesktop
      ? responsiveTilesPerSlide.xxLargeDesktop - 1
      : undefined,
  };
};
